import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { internalNameSettingKey } from '@/configuration/pages/ConfigurationPage/OrderTrackingPageConfiguration/constants'
import {
  getBlockDefaultSettings,
  getBlockDefinition,
  getBlockSettings,
  getValidBlockNumber,
} from '@/configuration/pages/ConfigurationPage/OrderTrackingPageConfiguration/helpers'

import {
  buildBlockSettingsFromSavedLayout,
  buildDefaultInternalName,
  getBlockAspectRatio,
  hasInternalNameSetting,
  haveBlockSettingsChanged,
} from './helpers'

const useBlockSettings = ({ savedLayout, layout }) => {
  const { t } = useTranslation()
  const initialBlockSettings = useMemo(() => {
    return savedLayout && buildBlockSettingsFromSavedLayout(savedLayout)
  }, [savedLayout])

  const [blockSettings, setBlockSettings] = useState({})

  useEffect(() => {
    if (initialBlockSettings) setBlockSettings(initialBlockSettings)
  }, [initialBlockSettings])

  const updateBlockSetting = (blockId, key, value) => {
    setBlockSettings(previousBlockSettings => {
      const settings = getBlockSettings(getBlockDefinition(blockId), previousBlockSettings[blockId])

      return { ...previousBlockSettings, [blockId]: { ...settings, [key]: value } }
    })
  }

  const nameBlock = blockId => {
    const definition = getBlockDefinition(blockId)

    if (!hasInternalNameSetting(definition)) return

    updateBlockSetting(blockId, internalNameSettingKey, buildDefaultInternalName(definition, layout, t))
  }

  const resizeBlock = (blockId, size) => {
    const { aspectRatioSetting } = getBlockDefinition(blockId)

    if (!aspectRatioSetting) return

    updateBlockSetting(
      blockId,
      aspectRatioSetting.key,
      getValidBlockNumber(getBlockAspectRatio(size), aspectRatioSetting)
    )
  }

  const resetBlockSettings = blockId => {
    setBlockSettings(previousBlockSettings => {
      return { ...previousBlockSettings, [blockId]: getBlockDefaultSettings(getBlockDefinition(blockId)) }
    })
  }

  const hasBlockSettingsChanges =
    Boolean(layout) && haveBlockSettingsChanged(layout, blockSettings, initialBlockSettings)

  return { blockSettings, updateBlockSetting, nameBlock, resizeBlock, resetBlockSettings, hasBlockSettingsChanges }
}

export default useBlockSettings
